import { ActionKeysForResource, ResourceKey } from "data/models";
import _ from "lodash";

export type PermissionsKey<R extends ResourceKey = ResourceKey> = {
  resource: R;
  action: ActionKeysForResource<R>;
  resourceDisplay: string;
  actionDisplay: string;
};

function createPermission<R extends ResourceKey>(
  resource: R,
  action: ActionKeysForResource<R>
): PermissionsKey<R> {
  const toDisplay = (s: string) => _.startCase(s.toLowerCase());

  return {
    resource: resource,
    action: action,
    resourceDisplay: toDisplay(resource),
    actionDisplay: toDisplay(action as string),
  };
}

export type AnyPermissionsKey = {
  [K in ResourceKey]: PermissionsKey<K>
}[ResourceKey];

export const PermissionsKeys = {
  invoicesRead: createPermission("invoices", "read"),
  subscriptionCreate: createPermission("subscription", "create"),
  subscriptionRead: createPermission("subscription", "read"),
  subscriptionUpdate: createPermission("subscription", "update"),
  subscriptionDelete: createPermission("subscription", "delete"),
  paymentMethodCreate: createPermission("payment_method", "create"),
  paymentMethodRead: createPermission("payment_method", "read"),
  paymentMethodUpdate: createPermission("payment_method", "update"),
  paymentMethodDelete: createPermission("payment_method", "delete"),
  overageCreate: createPermission("overage", "create"),
  overageRead: createPermission("overage", "read"),
  overageUpdate: createPermission("overage", "update"),
  overageDelete: createPermission("overage", "delete"),
  appsCreate: createPermission("apps", "create"),
  appsRead: createPermission("apps", "read"),
  appsUpdate: createPermission("apps", "update"),
  appsDelete: createPermission("apps", "delete"),
  clipCreate: createPermission("clip", "create"),
  clipRead: createPermission("clip", "read"),
  clipUpdate: createPermission("clip", "update"),
  clipDelete: createPermission("clip", "delete"),
  clipPayDollars: createPermission("clip", "payDollars"),
  clipOversized: createPermission("clip", "oversized"),
  batchCreate: createPermission("batch", "create"),
  batchRead: createPermission("batch", "read"),
  batchUpdate: createPermission("batch", "update"),
  batchDelete: createPermission("batch", "delete"),
  premiumDataCreate: createPermission("premium_data", "create"),
  premiumDataRead: createPermission("premium_data", "read"),
  premiumDataUpdate: createPermission("premium_data", "update"),
  premiumDataDelete: createPermission("premium_data", "delete"),
  groupsCreate: createPermission("groups", "create"),
  groupsRead: createPermission("groups", "read"),
  groupsUpdate: createPermission("groups", "update"),
  groupsDelete: createPermission("groups", "delete"),
  rolesCreate: createPermission("roles", "create"),
  rolesRead: createPermission("roles", "read"),
  rolesUpdate: createPermission("roles", "update"),
  rolesDelete: createPermission("roles", "delete"),
  permissionsCreate: createPermission("permissions", "create"),
  permissionsRead: createPermission("permissions", "read"),
  permissionsUpdate: createPermission("permissions", "update"),
  permissionsDelete: createPermission("permissions", "delete"),
  reportsCreate: createPermission("reports", "create"),
  reportsRead: createPermission("reports", "read"),
  reportsUpdate: createPermission("reports", "update"),
  reportsDelete: createPermission("reports", "delete"),
  membersCreate: createPermission("members", "create"),
  membersRead: createPermission("members", "read"),
  membersUpdate: createPermission("members", "update"),
  membersDelete: createPermission("members", "delete"),
  membersChangeOwner: createPermission("members", "changeOwner"),
  orgInfoCreate: createPermission("org_info", "create"),
  orgInfoRead: createPermission("org_info", "read"),
  orgInfoUpdate: createPermission("org_info", "update"),
  orgInfoDelete: createPermission("org_info", "delete"),
  creditsUnlimited: createPermission("credits", "unlimited"),
} as const;
